import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import css from './InstructionsToUse.module.css';
import { isHTMLString } from '../../util/data';

const InstructionsToUse = ({ content }) => {
    if(!content || typeof content !== 'string') return null;
    const [showFull, setShowFull] = useState(false);

    // Helper function to count paragraphs in HTML string
    const countParagraphs = (htmlString) => {
        if(typeof window === 'undefined') return 0;
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlString;
        return tempDiv.querySelectorAll('p').length;
    };

    // Helper function to truncate HTML content
    const truncateHTML = (htmlString, maxLength = 1000) => {
        if(typeof window === 'undefined') return htmlString;
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlString;
        let currentLength = 0;
        let truncatedContent = '';
        const walkDOM = (node) => {
            if (currentLength >= maxLength) return;
            if (node.nodeType === Node.TEXT_NODE) {
                const text = node.textContent;
                const remainingLength = maxLength - currentLength;
                if (currentLength + text.length <= maxLength) {
                    truncatedContent += text;
                    currentLength += text.length;
                } else {
                    truncatedContent += text.substring(0, remainingLength);
                    currentLength = maxLength;
                }
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const tagName = node.tagName.toLowerCase();
                truncatedContent += `<${tagName}`;
                for (let i = 0; i < node.attributes.length; i++) {
                    const attr = node.attributes[i];
                    truncatedContent += ` ${attr.name}="${attr.value}"`;
                }
                truncatedContent += '>';
                for (let i = 0; i < node.childNodes.length; i++) {
                    if (currentLength >= maxLength) break;
                    walkDOM(node.childNodes[i]);
                }
                if (currentLength < maxLength) {
                    truncatedContent += `</${tagName}>`;
                }
            }
        };
        walkDOM(tempDiv);
        return truncatedContent;
    };

    // Determine if truncation is needed
    const shouldTruncate = useMemo(() => {
        if (!content) return false;
        if (isHTMLString(content)) {
            const paragraphCount = countParagraphs(content);
            const charCount = content.replace(/<[^>]*>/g, '').length;
            return charCount > 1000 || paragraphCount > 3;
        } else {
            const charCount = content.length;
            const paragraphCount = content?.match(/\n\s*\n/g) || [].length + 1;
            return charCount > 1000 || paragraphCount > 3;
        }
    }, [content]);

    // Get truncated content
    const truncatedContent = useMemo(() => {
        if (!shouldTruncate || showFull) return content;
        if (isHTMLString(content)) {
            return truncateHTML(content, 1000);
        } else {
            return content.length > 1000 ? content.substring(0, 1000) + '...' : content;
        }
    }, [content, shouldTruncate, showFull]);

    return (
        <div className={css.panel}>
            <h4 className={css.heading}>Instructions to Use</h4>
            <div className={css.content}>
                {isHTMLString(truncatedContent) ? (
                    <div dangerouslySetInnerHTML={{ __html: truncatedContent }} />
                ) : (
                    <pre>{truncatedContent}</pre>
                )}
                {shouldTruncate && (
                    <button
                        className={css.readMoreButton}
                        onClick={() => setShowFull((v) => !v)}
                        type="button"
                    >
                        {showFull ? 'Read Less' : 'Read More'}
                    </button>
                )}
            </div>
        </div>
    );
};

InstructionsToUse.propTypes = {
    content: PropTypes.string.isRequired,
};

export default InstructionsToUse; 